import { AlertTriangle, BookOpen, Bug, Eye, ExternalLink, Leaf, Search, ShieldAlert, Sparkles, Sprout, CloudRain, FlaskConical, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { KIND_LABEL, MODEL_TYPE_LABEL } from '../../utils/pestRisk';
import { formatDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { PestModel, PestProfile } from '../../types/pest';
import { TYPE_ICON } from './pestModelDefaults';

/**
 * The grower-facing reading of a pest: what it looks like, how it lives, when
 * it strikes and what to do about it. Everything here is display text from the
 * researched profile — the forecast itself runs off the model parameters.
 */
export default function PestProfileView({ pest, onResearch }: {
  pest: PestModel;
  /** Offered when the viewer may (re)research this pest. */
  onResearch?: () => void;
}) {
  const orgTz = useOrgTimezone();
  const p: PestProfile = pest.profile ?? {};
  const mgmt = p.management ?? {};
  const hasProfile = !!pest.profile && (
    (p.symptoms?.length ?? 0) > 0 || !!p.lifecycle || !!p.favourable_conditions || (p.monitoring?.length ?? 0) > 0
  );

  return (
    <div className="space-y-4">
      {/* Identity */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600">{KIND_LABEL[pest.kind]}</span>
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 inline-flex items-center gap-1">{TYPE_ICON[pest.model_type]} {MODEL_TYPE_LABEL[pest.model_type]}</span>
            {p.notifiable && <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Notifiable</span>}
          </div>
          {pest.description && <p className="text-sm text-neutral-700 leading-relaxed">{pest.description}</p>}
          {(p.other_names?.length ?? 0) > 0 && (
            <p className="text-xs text-neutral-500"><span className="font-medium">Also known as:</span> {p.other_names!.join(', ')}</p>
          )}
          {p.australian_status && (
            <p className="text-xs text-neutral-500"><span className="font-medium">In Australia:</span> {p.australian_status}</p>
          )}
          {(pest.host_crop_type_names?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {pest.host_crop_type_names!.map((h) => (
                <span key={h} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700"><Sprout className="h-3 w-3" />{h}</span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {pest.recommended_actions && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> When risk is high</div>
          <p className="text-sm text-amber-900/90 mt-1 whitespace-pre-line">{pest.recommended_actions}</p>
        </div>
      )}

      {!hasProfile ? (
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpen className="h-8 w-8 mx-auto mb-3 text-neutral-300" />
            <p className="text-neutral-600 font-medium">No field guide for this pest yet</p>
            <p className="text-sm text-neutral-400 mt-1 max-w-md mx-auto">Symptoms, lifecycle, monitoring and management can be researched for you and reviewed before saving.</p>
            {onResearch && (
              <button onClick={onResearch} className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700">
                <Sparkles className="h-4 w-4" /> Research with AI
              </button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section icon={<Eye className="h-4 w-4" />} title="What to look for" items={p.symptoms} />
            <Section icon={<Search className="h-4 w-4" />} title="Monitoring" items={p.monitoring} />
            <Section icon={<Bug className="h-4 w-4" />} title="Lifecycle" text={p.lifecycle} />
            <Section icon={<CloudRain className="h-4 w-4" />} title="Conditions that favour it" text={p.favourable_conditions} />
          </div>

          {((mgmt.cultural?.length ?? 0) + (mgmt.biological?.length ?? 0) + (mgmt.chemical?.length ?? 0)) > 0 && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold text-neutral-800 mb-3">Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <MgmtList icon={<Leaf className="h-4 w-4 text-emerald-600" />} title="Cultural" items={mgmt.cultural} />
                  <MgmtList icon={<Bug className="h-4 w-4 text-sky-600" />} title="Biological" items={mgmt.biological} />
                  <MgmtList icon={<FlaskConical className="h-4 w-4 text-violet-600" />} title="Chemical" items={mgmt.chemical} />
                </div>
                {(mgmt.chemical?.length ?? 0) > 0 && (
                  <p className="text-xs text-neutral-400 mt-3">Chemical options are by mode-of-action group only. Rates, withholding periods and permitted crops come from the registered label.</p>
                )}
              </CardContent>
            </Card>
          )}

          {(p.lookalikes?.length ?? 0) > 0 && <Section icon={<AlertTriangle className="h-4 w-4" />} title="Easily confused with" items={p.lookalikes} />}
        </>
      )}

      {(p.sources?.length ?? 0) > 0 && (
        <div className="text-xs text-neutral-500 space-y-1">
          <div className="font-medium text-neutral-600">Sources</div>
          <ul className="space-y-0.5">
            {p.sources!.map((s) => (
              <li key={s.url} className="min-w-0">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-1 text-primary-700 hover:underline break-words [overflow-wrap:anywhere]">
                  <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0" />{s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.researched_at && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-400">
          <Sparkles className="h-3.5 w-3.5" />
          <span>Researched by AI on {formatDateInTz(p.researched_at, orgTz)} and reviewed on save. Check anything you act on against the sources.</span>
          {onResearch && (
            <button onClick={onResearch} className="inline-flex items-center gap-1 text-primary-700 hover:underline"><RefreshCw className="h-3 w-3" /> Research again</button>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, items, text }: { icon: React.ReactNode; title: string; items?: string[]; text?: string | null }) {
  if (!(items && items.length) && !text) return null;
  return (
    <Card>
      <CardContent className="pt-5">
        <h3 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">{icon}{title}</h3>
        {text && <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{text}</p>}
        {items && items.length > 0 && (
          <ul className="space-y-1.5">
            {items.map((s, i) => <li key={i} className="text-sm text-neutral-700 leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-neutral-300">{s}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MgmtList({ icon, title, items }: { icon: React.ReactNode; title: string; items?: string[] }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">{icon}{title}</div>
      {items && items.length > 0 ? (
        <ul className="space-y-1.5">{items.map((s, i) => <li key={i} className="text-sm text-neutral-700 leading-relaxed">{s}</li>)}</ul>
      ) : <p className="text-sm text-neutral-400">None listed.</p>}
    </div>
  );
}
